import { create } from 'zustand';
import {
  skillsIntelligenceApi,
  type ActivityEvent,
  type CoverageRow,
  type DriftCase,
  type ClientCoverage,
  type ArchetypePerformance,
  type VersionPerformance,
  type OptimizationProposal,
  type LoopStatus,
} from '@/services/api';
import { DEFAULT_ORG } from './skillRegistry';

interface ActivityState {
  events: ActivityEvent[];
  total: number;
  loading: boolean;
  error: string | null;
  fetch: (orgUid?: string, limit?: number, clientUid?: string) => Promise<void>;
}

export const useActivityStore = create<ActivityState>((set) => ({
  events: [],
  total: 0,
  loading: false,
  error: null,
  fetch: async (orgUid = DEFAULT_ORG, limit = 40, clientUid?: string) => {
    set({ loading: true, error: null });
    try {
      const res = await skillsIntelligenceApi.getActivity(orgUid, limit, clientUid);
      set({ events: res.events, total: res.total });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load activity' });
    } finally {
      set({ loading: false });
    }
  },
}));

interface CoverageState {
  skills: string[];
  tools: string[];
  rows: CoverageRow[];
  platformDefaults: Record<string, number>;
  loading: boolean;
  error: string | null;
  fetch: (orgUid?: string) => Promise<void>;
}

export const useCoverageStore = create<CoverageState>((set) => ({
  skills: [],
  tools: [],
  rows: [],
  platformDefaults: {},
  loading: false,
  error: null,
  fetch: async (orgUid = DEFAULT_ORG) => {
    set({ loading: true, error: null });
    try {
      const res = await skillsIntelligenceApi.getCoverage(orgUid);
      set({ skills: res.skills, tools: res.tools, rows: res.rows, platformDefaults: res.platform_defaults });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load coverage' });
    } finally {
      set({ loading: false });
    }
  },
}));

interface DriftState {
  cases: DriftCase[];
  totalDrift: number;
  loading: boolean;
  error: string | null;
  fetch: (orgUid?: string) => Promise<void>;
}

export const useDriftStore = create<DriftState>((set) => ({
  cases: [],
  totalDrift: 0,
  loading: false,
  error: null,
  fetch: async (orgUid = DEFAULT_ORG) => {
    set({ loading: true, error: null });
    try {
      const res = await skillsIntelligenceApi.getVersionDrift(orgUid);
      set({ cases: res.drift_cases, totalDrift: res.total_drift });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load drift data' });
    } finally {
      set({ loading: false });
    }
  },
}));

interface ClientCoverageState {
  clients: ClientCoverage[];
  coreTools: string[];
  loading: boolean;
  error: string | null;
  fetch: (orgUid?: string) => Promise<void>;
}

export const useClientCoverageStore = create<ClientCoverageState>((set) => ({
  clients: [],
  coreTools: [],
  loading: false,
  error: null,
  fetch: async (orgUid = DEFAULT_ORG) => {
    set({ loading: true, error: null });
    try {
      const res = await skillsIntelligenceApi.getClientCoverage(orgUid);
      set({ clients: res.clients, coreTools: res.core_tools });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load client coverage' });
    } finally {
      set({ loading: false });
    }
  },
}));

interface PerformanceState {
  archetypes: ArchetypePerformance[];
  versions: VersionPerformance[];
  proposals: OptimizationProposal[];
  loopStatuses: LoopStatus[];
  generatedAt: string | null;
  loading: boolean;
  error: string | null;
  selectedClient: string | null;
  setSelectedClient: (c: string | null) => void;
  fetch: (orgUid?: string, clientUid?: string) => Promise<void>;
}

export const usePerformanceStore = create<PerformanceState>((set, get) => ({
  archetypes: [],
  versions: [],
  proposals: [],
  loopStatuses: [],
  generatedAt: null,
  loading: false,
  error: null,
  selectedClient: null,
  setSelectedClient: (c) => {
    set({ selectedClient: c });
  },
  fetch: async (orgUid = DEFAULT_ORG, clientUid?: string) => {
    const effectiveClient = clientUid ?? get().selectedClient ?? undefined;
    set({ loading: true, error: null });
    try {
      const [archRes, verRes, propRes, loopRes] = await Promise.allSettled([
        skillsIntelligenceApi.getArchetypePerformance(orgUid, effectiveClient),
        skillsIntelligenceApi.getVersionPerformance(orgUid, effectiveClient),
        skillsIntelligenceApi.getProposals(orgUid),
        skillsIntelligenceApi.getLoopStatus(orgUid),
      ]);

      set({
        archetypes: archRes.status === 'fulfilled' ? archRes.value.archetypes : [],
        versions: verRes.status === 'fulfilled' ? verRes.value.versions : [],
        proposals: propRes.status === 'fulfilled' ? propRes.value.proposals : [],
        loopStatuses: loopRes.status === 'fulfilled' ? loopRes.value.clients : [],
        generatedAt: propRes.status === 'fulfilled' ? propRes.value.generated_at : null,
      });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load performance data' });
    } finally {
      set({ loading: false });
    }
  },
}));
